import { useRef, useState, useEffect } from 'react';
import { ColDef, GridApi, ICellRendererParams, IDatasource, IGetRowsParams } from 'ag-grid-community';
import queryString from 'query-string';

import Session from 'modules/Session';
import { fetchCreativesFromAllMarkets } from 'modules/api/creativesAdministration';
import { Filter } from 'store/pagesFilters/types';
import { cancelFunctions } from 'utils/cancelFunctions';
import { getDealIdsStringFromMarkets } from 'utils/dealIdsFromMarkets';
import HeaderRenderer from 'components/common/HeaderRenderer';
import ImageRenderer from 'components/common/ImageRenderer';
import { Creative } from 'components/common/types/Creative.types';
import { CreativeAdministrationPageFilters } from '../Filters/Filters.types';

type UseCreativesProps = {
  setSelectedCreatives: (creatives: Creative[]) => void;
  areMarketsUpdated: boolean;
  filters: Filter<CreativeAdministrationPageFilters>;
};

type UseCreatives = {
  columnDefs: ColDef[];
  pageSize: number;
  handlePageSizeChange: (numberOfItems: number) => void;
  handleSelectionChanged: (event: { api: GridApi }) => void;
  getTableApi: (tableApi: GridApi) => void;
};

const useCreatives = ({ setSelectedCreatives, areMarketsUpdated, filters }: UseCreativesProps): UseCreatives => {
  const tableApiRef = useRef<GridApi>();
  const [pageSize, setPageSize] = useState(parseInt(Session.getPageSizeByKey('creativeAdmin')) || 10);

  const getFilterQuerystring = (passedFilters: Filter<CreativeAdministrationPageFilters>): string => {
    const filtersMap = {
      withMarket: passedFilters?.marketStatus,
      environment: passedFilters.market?.name,
      dspName: passedFilters.dsps?.map((item) => item.name),
      externalId: passedFilters.creativeIds?.map((item) => item.name),
      dealId: passedFilters.dealIds?.map((item) => item.name),
    };

    return queryString.stringify(filtersMap, { skipEmptyString: true, arrayFormat: 'comma' });
  };

  const getDataSource = (): IDatasource => {
    return {
      getRows: async (params: IGetRowsParams) => {
        try {
          const pageNumber = params.startRow / pageSize;
          const { content, totalElements } = await fetchCreativesFromAllMarkets(
            cancelFunctions,
            getFilterQuerystring(filters),
            `&page=${pageNumber}&size=${pageSize}`,
          );

          if (totalElements === 0) {
            tableApiRef.current?.showNoRowsOverlay();
          } else {
            tableApiRef.current?.hideOverlay();
          }

          params.successCallback(content, totalElements);
        } catch {} // eslint-disable-line no-empty
      },
    };
  };

  const getTableApi = (tableApi: GridApi): void => {
    tableApiRef.current = tableApi;
    tableApiRef.current?.setDatasource(getDataSource());
  };

  const handlePageSizeChange = (numberOfItems: number): void => {
    setPageSize(numberOfItems);
    Session.setPageSizeByKey('creativeAdmin', numberOfItems);
    setSelectedCreatives([]);
  };

  const handleSelectionChanged = (event: { api: GridApi }): void => {
    setSelectedCreatives(event.api.getSelectedRows());
  };

  const columnDefs: ColDef[] = [
    {
      headerName: 'Creative',
      field: 'thumbnail',
      minWidth: 180,
      headerTooltip: 'Creative',
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      headerComponent: HeaderRenderer,
      headerComponentParams: {
        onSelectionChanged: handleSelectionChanged,
      },
      checkboxSelection: true,
      cellRenderer: ImageRenderer,
      cellRendererParams: (params: ICellRendererParams) => ({
        link: params.data?.id ? `/content-management/previewCreative/${params.data.id}` : '',
      }),
    },
    { headerName: 'ID', field: 'externalId', minWidth: 215, headerTooltip: 'ID' },
    {
      headerName: 'DSP',
      field: 'dsp',
      minWidth: 337,
      headerTooltip: 'DSP',
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.data) return '';
        return params.data.dsp?.name;
      },
    },
    {
      headerName: 'Deal ID',
      field: 'market',
      minWidth: 368,
      headerTooltip: 'Deal ID',
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.data?.market) return '-';
        return getDealIdsStringFromMarkets(params.data.market);
      },
    },
    {
      headerName: 'Market',
      field: 'market',
      minWidth: 240,
      headerTooltip: 'Market',
      resizable: false,
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.data?.market) return '-';
        const markets = params.data.market.map((m: { environment: string }) => m?.environment || '');

        return markets.length ? `${markets.join(',')}` : '';
      },
    },
  ];

  useEffect(() => {
    if (tableApiRef.current) {
      tableApiRef.current.setDatasource(getDataSource());
      setSelectedCreatives([]);
    }
  }, [filters, areMarketsUpdated]);

  return { columnDefs, pageSize, handlePageSizeChange, handleSelectionChanged, getTableApi };
};

export default useCreatives;
